import { getAllDirectAgentChildren, SimpleAgentEntity } from '@vgrubs/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

const ADMIN_AGENT_CHILDREN_PREFIX = '/v1/agents/direct-childs';
const PRELOAD_ROOT_MARGIN = '1000px';

const fetchAgentChildren = async ({
  pageParam = 1,
  agentId,
  query = '',
}: {
  pageParam?: number;
  agentId: string | number;
  query?: string;
}) => {
  const { data } = await getAllDirectAgentChildren(agentId, {
    limit: 20,
    page: pageParam,
    query: query || undefined,
  });

  const agents: SimpleAgentEntity[] = data?.data ?? [];
  const nextPage = agents.length === 20 ? pageParam + 1 : undefined;

  return {
    agents,
    nextPage,
  };
};

export const useAgentChildrenInfiniteScroll = ({
  agentId,
  initialQuery = '',
}: {
  agentId: string | number;
  initialQuery?: string;
}) => {
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const observerRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearch = useMemo(() => debounce(setQuery, 500), []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: [ADMIN_AGENT_CHILDREN_PREFIX, agentId, query],
    queryFn: ({ pageParam = 1 }) =>
      fetchAgentChildren({ pageParam, agentId, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const agents: SimpleAgentEntity[] =
    data?.pages.flatMap((page) => page.agents) ?? [];

  return {
    data,
    agents,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    status,
    refetch,
  };
};
